/**
 * Renders the agent's `show_table` output as a real table, so query results
 * reach the user as structured rows instead of markdown retyped by the model.
 *
 * The payload is the tool output (server-built from query results), so only a
 * completed, well-formed output renders; anything else shows a short error.
 */
import React from 'react';
import Column from '@amzn/meridian/column';
import DataTable, { DataTableBody, DataTableCell, DataTableHead, DataTableHeader, DataTableRow } from '@amzn/meridian/data-table';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { type ChatMessageProps } from './types';
import { formatTableCell, isTableCell, isTableResult, NUMERIC_FORMATS, type TableResult } from './visualCells';

type TableOutput = TableResult;

const isTableOutput = (value: unknown): value is TableOutput => {
  if (!isTableResult(value) || value.columns.length === 0) return false;
  const columnCount = value.columns.length;
  return value.rows.every((row: unknown) =>
    Array.isArray(row) && row.length === columnCount && row.every(isTableCell));
};

export const TableMessage: React.FC<ChatMessageProps> = ({ part, index }) => {
  const { t } = useTranslation();
  const titleId = React.useId();
  if (part.state !== 'output-available' && part.state !== 'output-error') return null;
  const table = part.state === 'output-available' && isTableOutput(part.output) ? part.output : null;
  if (!table) return <Text color="error" data-testid="show-table-error" key={index} type="b100">{t('chat_table_error')}</Text>;
  return (
    <Column key={index} spacing="200" width="100%">
      <Text id={titleId} type="h100">{table.title}</Text>
      <div data-testid="chat-table" style={{ maxHeight: 400, overflowX: 'auto', overflowY: 'auto', width: '100%' }}>
        <DataTable aria-labelledby={titleId} showRowDividers={true} size="small" stickyHeaderRows={true} width="100%">
          <DataTableHead backgroundColor="secondary">
            <DataTableRow>
              {table.columns.map((column) => (
                <DataTableHeader key={column.key} textAlign={NUMERIC_FORMATS.has(column.format) ? 'end' : 'start'}>{column.label}</DataTableHeader>
              ))}
            </DataTableRow>
          </DataTableHead>
          <DataTableBody>
            {table.rows.length === 0 && (
              <DataTableRow>
                <DataTableCell colSpan={table.columns.length}>{t('table_no_results')}</DataTableCell>
              </DataTableRow>
            )}
            {table.rows.map((row, rowIndex) => (
              <DataTableRow key={rowIndex}>
                {table.columns.map((column, columnIndex) => (
                  <DataTableCell key={column.key} textAlign={NUMERIC_FORMATS.has(column.format) ? 'end' : 'start'}>
                    {formatTableCell(row[columnIndex], column)}
                  </DataTableCell>
                ))}
              </DataTableRow>
            ))}
          </DataTableBody>
        </DataTable>
      </div>
      {table.caption && <Text color="secondary" type="b200">{table.caption}</Text>}
      {table.truncated && <Text color="secondary" type="b200">{t('chat_table_truncated', { count: table.rows.length })}</Text>}
    </Column>
  );
};
